import type { Dispatch } from 'react';
import type { StoreApi } from 'zustand';

import { focusElementWithRetry } from '../../../../forms/date-time-picker/utils/focusDateTimeInput.js';
import type { FormFieldMessagesReducerAction } from '../../../../forms/forms-core/state/formfield-messages-state-reducer.js';
import {
  getTimeValueFromSimpleValue,
  toCalendarValue,
} from '../../../../forms/forms-core/time/helpers.js';
import { getTimeValue } from '../../../../forms/forms-core/time/timeParser.js';
import { type TimeValue } from '../../../types/time.js';
import { isTimeValue } from '../../helpers.js';
import { validate } from '../../validation.js';
import type {
  DateTimePickerValueStore,
  DateTimePickerValueStoreActions,
} from '../dateTimePicker.types.js';

/**
 * Creates actions for the DateTimePicker store using the slice action handlers.
 *  @internal
 */
export function getDateTimePickerActions(
  formFieldMessagesDispatch?: Dispatch<FormFieldMessagesReducerAction>,
  onChangeProp?: ((value: TimeValue | null | '') => void) | undefined,
  isControlled?: boolean,
) {
  const actions = (
    set: StoreApi<DateTimePickerValueStore>['setState'],
  ): DateTimePickerValueStoreActions => {
    return {
      setCalendarValue: (inputValue) => {
        set((state) => {
          // DateTimePicker is single-date only — TimeValue expressions are not applicable.
          if (isTimeValue(inputValue)) {
            return state;
          }

          const newTimeValue =
            getTimeValueFromSimpleValue(
              inputValue,
              state.timeOffset,
              undefined,
              undefined,
              state.min,
              state.max,
              state.precision,
            ) ?? null;

          const calendarError = validate(newTimeValue, state.min, state.max, {
            formMessageDispatch: formFieldMessagesDispatch,
            formerError: state.dateTimePickerError,
            precision: state.precision,
            showError: false,
            checkOrder: true,
            ref: state.dateTimePickerRef,
            expectedFormat: state.formatString,
            valueMode: 'single',
            required: state.required,
          });

          state.dateTimePickerRef?.current?.close();
          focusElementWithRetry(
            () =>
              state.dateTimeInputRef.current?.spinButtonHandlers.get('hour')
                ?.element.current,
            state.dateTimeInputRef,
          );

          const calendarTimeValue = toCalendarValue(
            newTimeValue,
            state.precision,
          );
          return {
            calendarValue: calendarTimeValue,
            dateTimePickerValue: newTimeValue,
            dateTimePickerError: calendarError,
            calendarError,
          };
        });
      },
      setDateTimePickerValue: (value) => {
        return set((state) => {
          const newValue = getTimeValue(value, state.precision) ?? null;

          // we would expect a timevalue but as it isn't a complete value then parser returns null.
          const isPartialValue = newValue?.absoluteDate === '';

          // In case of interrim values we want to validate and show errors but not update the calendar value.
          // To still inform about the error state it triggers the onChange with an empty string.
          if (isPartialValue) {
            const errorKey = validate(newValue, state.min, state.max, {
              formMessageDispatch: formFieldMessagesDispatch,
              formerError: state.dateTimePickerError,
              precision: state.precision,
              showError: false,
              checkOrder: true,
              ref: state.dateTimePickerRef,
              expectedFormat: state.formatString,
              valueMode: 'single',
            });

            onChangeProp?.('');

            return {
              calendarError: errorKey,
              dateTimePickerError: errorKey,
            };
          }

          onChangeProp?.(newValue);

          const calendarTimeValue = toCalendarValue(newValue, state.precision);

          const dateTimePickerError = validate(newValue, state.min, state.max, {
            formMessageDispatch: formFieldMessagesDispatch,
            formerError: state.dateTimePickerError,
            precision: state.precision,
            showError: false,
            checkOrder: false,
            ref: state.dateTimePickerRef,
            expectedFormat: state.formatString,
            valueMode: 'single',
            required: state.required,
          });

          if (isControlled) {
            return { calendarValue: calendarTimeValue, dateTimePickerError };
          }

          return {
            dateTimePickerValue: newValue,
            calendarValue: calendarTimeValue,
            calendarError: dateTimePickerError,
            dateTimePickerError,
          };
        });
      },
    };
  };

  return actions;
}
